import { PromptAbortedError, PromptRequiredError } from '../../errors'
import type { Answers, Prompt, ValidationContext } from '../../templates/manifest'
import { VALIDATORS } from '../../templates/manifest'
import { interpolate } from '../../templates/render'

// Turning a template's declared prompts into a complete set of answers, without asking anybody anything.
//
// Everything that can be decided from what the caller already supplied — a prefill, a fixed value, a
// default — is decided here. Only a prompt with no answer left needs a human, and that goes out through
// `ask`. A caller with no `ask` gets PromptRequiredError naming the prompt, which is what makes a scripted
// or programmatic scaffold possible: supply every answer and nothing ever needs a terminal.

/** Shared with the standalone confirmations in `cli/prompts/confirm`, so both read a yes/no the same way. */
export const TRUTHY = ['y', 'yes', 'true', '1']
export const FALSY = ['n', 'no', 'false', '0']

export interface AskRequest {
  prompt: Prompt
  /** What the answer would be if the user just pressed Enter. */
  fallback: string | boolean
  /** Returns a message to show before asking again, or null when the value is acceptable. */
  validate: (value: string) => string | null
}

export type Ask = (request: AskRequest) => Promise<string | boolean>

export interface ResolveAnswersOptions {
  /** Pre-supplied answers (CLI flags). A prompt with a prefill is not asked. */
  prefilled?: Answers
  /** Answers the template fixes rather than asking for — see `values` in the manifest. */
  constants?: Answers
  /** Ask nothing; take every default. */
  acceptDefaults?: boolean
  /** The project being scaffolded into; validators need it to tell whether an id is taken. */
  projectRoot?: string
  /** How to ask a human. Omitted means nothing can be asked, and an unanswered prompt is an error. */
  ask?: Ask
}

/** Resolve a prompt's declared default, which may interpolate answers collected before it. */
export function resolveDefault(prompt: Prompt, answers: Answers): string | boolean {
  if (prompt.type === 'boolean' || prompt.type === 'choice') {
    return prompt.default
  }

  return interpolate(prompt.default, answers, `default of "${ prompt.name }"`)
}

/**
 * A prefill arrives as a raw string when it came from a flag; anything boolean has to interpret it.
 *
 * Skipping this for a fixed value would be quietly wrong rather than merely untidy: `--set flag=false`
 * would store the STRING "false", which is truthy, and the `{{#if flag}}` block would survive.
 */
export function coerceBoolean(label: string, value: string | boolean): boolean {
  if (typeof value === 'boolean') {
    return value
  }

  const normalized = value.trim().toLowerCase()

  if (TRUTHY.includes(normalized)) {
    return true
  }

  if (FALSY.includes(normalized)) {
    return false
  }

  throw new PromptAbortedError(`${ label }: expected true or false, got "${ value }"`)
}

function validator(prompt: Prompt, context: ValidationContext): (value: string) => string | null {
  return value => {
    if (prompt.type !== 'string' || !prompt.validate) {
      return null
    }

    const failure = VALIDATORS[prompt.validate](value, context)

    return failure && `${ prompt.label } ${ failure }`
  }
}

/** Run a template's declared prompts in order, honouring prefills and `--yes`. */
export async function resolveAnswers(
  prompts: Prompt[],
  {
    prefilled = {},
    constants = {},
    acceptDefaults = false,
    projectRoot = process.cwd(),
    ask,
  }: ResolveAnswersOptions = {}
): Promise<Answers> {
  const context: ValidationContext = { projectRoot }
  // Fixed values first, so a prompt default may interpolate one; a prompt of the same name wins over it.
  const answers: Answers = { ...constants }

  // A prefill naming neither a prompt nor a fixed value is a typo, not a no-op — silently ignoring it
  // would hand the user a service that quietly differs from what they asked for.
  const known = [...prompts.map(prompt => prompt.name), ...Object.keys(constants)]
  const unknown = Object.keys(prefilled).filter(name => !known.includes(name))

  if (unknown.length > 0) {
    throw new PromptAbortedError(
      `This template has no setting named ${ unknown.map(name => `"${ name }"`).join(', ') }. ` +
      `It accepts: ${ known.join(', ') }`
    )
  }

  for (const prompt of prompts) {
    const fallback = resolveDefault(prompt, answers)
    const validate = validator(prompt, context)

    if (prompt.name in prefilled) {
      const supplied = prefilled[prompt.name]

      if (prompt.type === 'boolean') {
        answers[prompt.name] = coerceBoolean(prompt.label, supplied)

        continue
      }

      if (prompt.type === 'choice') {
        const values = prompt.options.map(option => option.value)

        if (!values.includes(String(supplied))) {
          throw new PromptAbortedError(`${ prompt.label }: "${ supplied }" is not one of ${ values.join(', ') }`)
        }

        answers[prompt.name] = String(supplied)

        continue
      }

      const failure = validate(String(supplied))

      if (failure) {
        throw new PromptAbortedError(failure)
      }

      answers[prompt.name] = String(supplied)

      continue
    }

    if (acceptDefaults) {
      // Validated like any other answer: under `--yes` a default service id can still be taken, and the
      // failure should read the same as it would have when typed.
      const failure = typeof fallback === 'string' ? validate(fallback) : null

      if (failure) {
        throw new PromptAbortedError(failure)
      }

      answers[prompt.name] = fallback

      continue
    }

    if (!ask) {
      throw new PromptRequiredError(
        prompt.name,
        `"${ prompt.name }" has no answer and there is no way to ask for one. ` +
        'Supply it up front, or accept the defaults.'
      )
    }

    answers[prompt.name] = await ask({ prompt, fallback, validate })
  }

  // A `--set` for a fixed value overrides it; prompts have already had their turn above. The manifest's
  // own value tells us the intended type, so a boolean stays a boolean.
  for (const [name, value] of Object.entries(prefilled)) {
    if (prompts.some(prompt => prompt.name === name)) {
      continue
    }

    answers[name] = typeof constants[name] === 'boolean' ? coerceBoolean(name, value) : value
  }

  return answers
}
